import { FeedAttachmentRow } from "./FeedAttachmentRow";
import { PunttAvatar } from "./PunttAvatar";

import { AvatarWithInitials } from "../../../components/AvatarWithInitials";
import {
  type ProjectFeedAttachment,
  type ProjectFeedEvent,
} from "../../../services/askPunttProjectFeed.types";

function formatTime(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleTimeString(undefined, {
    hour: "numeric",
    minute: "2-digit",
  });
}

function actorDisplayName(event: ProjectFeedEvent): string {
  const actor = event.actor;
  if (actor?.name) return actor.name;
  if (actor?.kind === "puntt") return "Puntt";
  if (actor?.kind === "system") return "Puntt";
  return "Member";
}

/**
 * Generic feed post: author row (avatar, name, role subtitle, timestamp),
 * the post body, and any attachment rows. Specialized event types (imports,
 * memory, suggestions, coaching, version diffs) render their own cards in
 * renderProjectFeedCard; FeedPost is the fallback for human + Puntt narrative
 * posts (project_created, owner_kickoff, version_added, review_started,
 * user_post, …).
 */
export function FeedPost({
  event,
  onOpenAttachment,
}: {
  event: ProjectFeedEvent;
  onOpenAttachment?: (attachment: ProjectFeedAttachment) => void;
}) {
  const actor = event.actor;
  const isPuntt = actor?.kind === "puntt" || actor?.kind === "system";
  const name = actorDisplayName(event);
  const role = actor?.role ?? (isPuntt ? "AI reviewer" : undefined);
  const time = formatTime(event.createdAt);

  return (
    <article
      className="flex gap-3"
      data-testid={`feed-post-${event._id}`}
      data-event-type={event.type}
    >
      <div className="shrink-0 pt-0.5">
        {isPuntt ? (
          <PunttAvatar size={8} />
        ) : (
          <AvatarWithInitials
            size={8}
            name={name}
            avatar={actor?.avatar ?? undefined}
          />
        )}
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-baseline gap-2">
          <span className="text-[13px] font-semibold text-puntt-neutral-gray-12">
            {name}
          </span>
          {role && (
            <span
              className="text-[11px] text-puntt-neutral-gray-10"
              data-testid={`feed-post-role-${event._id}`}
            >
              {role}
            </span>
          )}
          {time && (
            <span
              className="shrink-0 text-[11px] text-puntt-neutral-gray-9"
              data-testid={`feed-post-timestamp-${event._id}`}
            >
              {time}
            </span>
          )}
        </div>
        {event.body && (
          <p className="mt-1 whitespace-pre-wrap text-[13px] leading-relaxed text-puntt-neutral-gray-12">
            {event.body}
          </p>
        )}
        {event.attachments && event.attachments.length > 0 && (
          <div className="mt-2 flex flex-col gap-1.5">
            {event.attachments.map((a) => (
              <FeedAttachmentRow
                key={`${a.fileId}-${a.versionNumber ?? "x"}`}
                attachment={a}
                onOpen={onOpenAttachment}
              />
            ))}
          </div>
        )}
      </div>
    </article>
  );
}
